/* eslint-disable quote-props */
import React, { useState, useMemo } from 'react'

import {
	EuiButton,
	EuiFieldSearch,
	EuiListGroup,
	EuiListGroupItem,
	EuiPanel,
	EuiProgress,
	EuiTitle,
	EuiFlexGroup,
	EuiFlexItem,
	EuiSpacer,
} from '@equipmentshare/ds2'

import {
	getAddRoleModal,
	getEditRoleModal,
	getRemoveRoleModal
} from '../actionModals'
import { useDispatchSetSelectedModal } from 'contexts/modal-context/modal-context'

import { Role } from 'graphql'
import { TABLE_HEIGHT } from 'views/helpers/utils'
import { heapEvent } from '@equipmentshare/heaper'

export type RolesListProps = {
	selectedRole?: Role,
	setSelectedRole: ( r?: Role ) => void,
	roles: Role[],
	isLoading: boolean,
	refetchRoles: () => Promise<any>,
}

export default function RolesList( { selectedRole, setSelectedRole, roles, refetchRoles, isLoading }: RolesListProps ) {
	const [ search, setSearch ] = useState( '' )
	const setSelectedModal = useDispatchSetSelectedModal()

	const handleSearchChange = ( event: React.ChangeEvent<HTMLInputElement> ) => setSearch( event.currentTarget.value )

	const onAddClick = () => {
		heapEvent( 'Inventory - Permission Roles - Click - Add Role', {} )
		setSelectedModal( getAddRoleModal( async() => await refetchRoles() ) )
	}
	const onDeleteClick = ( role: Role ) => () => {
		heapEvent( 'Inventory - Edit Role Modal - Click - Delete', {} )
		setSelectedModal( getRemoveRoleModal( role, async() => {
			setSelectedRole( undefined )
			await refetchRoles()
		} ) )
	}
	const onEditClick = ( role: Role ) => () => {
		heapEvent( 'Inventory - Permission Roles - Click - Edit', {} )
		setSelectedModal( getEditRoleModal( role, onDeleteClick( role ) ) )
	}

	const filteredRoles = useMemo( () => {
		if ( search === '' ) {
			return roles
		}
		const lowerCaseSearch = search.toLowerCase()
		return roles.filter( ( role ) => role.name.toLowerCase().includes( lowerCaseSearch ) )
	}, [ search, roles ] )

	return (
		<div style={{ minWidth: '400px' }}>
			<EuiFlexGroup alignItems="center" gutterSize="xl">
				<EuiFlexItem grow={false}>
					<EuiTitle size="s"><h2>Roles</h2></EuiTitle>
				</EuiFlexItem>
				<EuiFlexItem grow={false}>
					<EuiButton
						iconType="plus"
						size="s"
						onClick={onAddClick}
						color="text"
					>
						Add role
					</EuiButton>
				</EuiFlexItem>
			</EuiFlexGroup>
			<EuiSpacer size="m" />
			<EuiPanel
				hasShadow={false}
				hasBorder
				paddingSize="s"
				className="eui-yScroll"
				style={{ height: TABLE_HEIGHT }}
			>
				<EuiFieldSearch
					id="search"
					value={search}
					onChange={handleSearchChange}
				/>
				{ isLoading && <EuiProgress size="xs" /> }
				<EuiListGroup>
					{ filteredRoles.map( ( role ) => (
						<EuiListGroupItem
							key={role.id}
							label={role.name}
							onClick={() => setSelectedRole( role )}
							isActive={role.id === selectedRole?.id}
							extraAction={{
								color: 'primary',
								onClick: onEditClick( role ),
								iconType: 'pencil',
								iconSize: 's',
								'aria-label': 'Edit',
							}}
						/>
					) ) }
				</EuiListGroup>
			</EuiPanel>
		</div>
	)
}
